import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  InputAdornment,
  Stack,
  IconButton,
} from "@mui/material";
import { FaArrowLeft, FaUserGraduate, FaHashtag } from "react-icons/fa";
import { toast } from "react-toastify";
import { API_URL } from "../config";

/* ─── Animated ring decoration ─── */
const Ring = ({ sx }) => (
  <motion.div
    style={{
      position: "absolute",
      borderRadius: "50%",
      border: "1.5px solid rgba(16,185,129,0.12)",
      pointerEvents: "none",
      ...sx,
    }}
    animate={{ scale: [1, 1.06, 1], opacity: [0.5, 0.8, 0.5] }}
    transition={{
      duration: 5 + Math.random() * 3,
      repeat: Infinity,
      ease: "easeInOut",
      delay: Math.random() * 2,
    }}
  />
);

const inputSx = {
  "& .MuiOutlinedInput-root": {
    color: "#1e293b",
    borderRadius: "14px",
    background: "#f8fafc",
    fontSize: "0.95rem",
    fontFamily: "'DM Sans', sans-serif",
    transition: "all 0.25s ease",
    "& fieldset": { borderColor: "#e2e8f0", borderWidth: "1.5px" },
    "&:hover fieldset": { borderColor: "#94a3b8" },
    "&.Mui-focused": {
      background: "#fff",
      boxShadow: "0 0 0 4px rgba(16,185,129,0.1)",
    },
    "&.Mui-focused fieldset": { borderColor: "#10b981", borderWidth: "2px" },
  },
};

/* ════════════════════════════════════════════════════════ */
export default function RegularStudentLogin() {
  const navigate = useNavigate();
  const [studCode, setStudCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    document.title = "Student Login | LiteUpper";
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Sora:wght@700;800&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }, []);

  const handleLogin = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!studCode.trim()) {
      setError("Please enter your student code.");
      return;
    }
    setError("");
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/login-S/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ studCode }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.detail || "Login failed. Please check your code.");
        return;
      }

      localStorage.setItem(
        "authToken",
        JSON.stringify({ access: data.access, refresh: data.refresh })
      );
      localStorage.setItem("student_id", data.student_id);
      toast.success("Login Successful");
      navigate(`/student-analysis/${data.student_id}`);
    } catch {
      setError("Server error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(145deg, #f0fdf6 0%, #f8fafc 50%, #f0f9ff 100%)",
        position: "relative",
        overflow: "hidden",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {/* Noise texture */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.025,
          pointerEvents: "none",
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Decorative blobs */}
      <Box
        sx={{
          position: "absolute",
          top: "-10%",
          right: "-8%",
          width: 380,
          height: 380,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(16,185,129,0.1) 0%, transparent 70%)",
          filter: "blur(40px)",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "5%",
          left: "-5%",
          width: 280,
          height: 280,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(56,189,248,0.1) 0%, transparent 70%)",
          filter: "blur(40px)",
          pointerEvents: "none",
        }}
      />

      {/* Rings */}
      <Ring
        sx={{
          width: 500,
          height: 500,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      />
      <Ring
        sx={{
          width: 700,
          height: 700,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      />

      {/* Grid */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.03,
          backgroundImage:
            "linear-gradient(#10b981 1px, transparent 1px), linear-gradient(90deg, #10b981 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          pointerEvents: "none",
        }}
      />

      {/* Back button */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        style={{ position: "absolute", top: 24, left: 24, zIndex: 10 }}
      >
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            color: "#475569",
            background: "rgba(255,255,255,0.9)",
            backdropFilter: "blur(8px)",
            boxShadow:
              "0 2px 12px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)",
            transition: "all 0.2s ease",
            "&:hover": {
              background: "#fff",
              color: "#0f172a",
              transform: "translateX(-2px)",
              boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
            },
          }}
        >
          <FaArrowLeft size={15} />
        </IconButton>
      </motion.div>

      <Container maxWidth="xs" sx={{ zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 28, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <Paper
            elevation={0}
            sx={{
              p: "40px 36px",
              borderRadius: "28px",
              textAlign: "center",
              background: "rgba(255,255,255,0.92)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255,255,255,0.95)",
              boxShadow:
                "0 24px 64px -12px rgba(15,23,42,0.08), 0 0 0 1px rgba(16,185,129,0.06)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Top accent */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: "15%",
                right: "15%",
                height: "2px",
                background:
                  "linear-gradient(90deg, transparent, #10b981, #34d399, transparent)",
                borderRadius: "0 0 4px 4px",
              }}
            />

            {/* Avatar */}
            <motion.div
              initial={{ scale: 0.5, opacity: 0, rotate: -10 }}
              animate={{ scale: 1, opacity: 1, rotate: 0 }}
              transition={{
                duration: 0.55,
                delay: 0.15,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "20px",
                  mx: "auto",
                  mb: 3,
                  background: "linear-gradient(135deg, #d1fae5, #a7f3d0)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow:
                    "0 8px 24px rgba(16,185,129,0.22), inset 0 1px 0 rgba(255,255,255,0.8)",
                  border: "1px solid rgba(16,185,129,0.2)",
                }}
              >
                <FaUserGraduate size={26} color="#059669" />
              </Box>
            </motion.div>

            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.7,
                  px: 1.6,
                  py: 0.5,
                  borderRadius: "20px",
                  mb: 2,
                  background:
                    "linear-gradient(135deg, rgba(16,185,129,0.1), rgba(52,211,153,0.1))",
                  border: "1px solid rgba(16,185,129,0.18)",
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "#10b981",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: "#059669",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                  }}
                >
                  Student Access
                </Typography>
              </Box>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
            >
              <Typography
                sx={{
                  color: "#0f172a",
                  fontWeight: 800,
                  fontSize: "1.65rem",
                  letterSpacing: "-0.7px",
                  lineHeight: 1.2,
                  mb: 0.8,
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                View Your Analysis
              </Typography>
              <Typography
                sx={{
                  color: "#64748b",
                  mb: 3.5,
                  fontWeight: 500,
                  fontSize: "0.88rem",
                  lineHeight: 1.5,
                }}
              >
                Enter your student code to see your performance
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.32 }}
            >
              <form onSubmit={handleLogin}>
                <Stack spacing={2.5}>
                  <TextField
                    fullWidth
                    placeholder="Enter Student Code"
                    value={studCode}
                    onChange={(e) => {
                      setStudCode(e.target.value);
                      setError("");
                    }}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    autoComplete="off"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <motion.div
                            animate={{ color: focused ? "#10b981" : "#94a3b8" }}
                            transition={{ duration: 0.2 }}
                          >
                            <FaHashtag size={14} />
                          </motion.div>
                        </InputAdornment>
                      ),
                    }}
                    sx={inputSx}
                  />

                  {/* Error */}
                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -8, height: 0 }}
                        animate={{ opacity: 1, y: 0, height: "auto" }}
                        exit={{ opacity: 0, y: -8, height: 0 }}
                        transition={{ duration: 0.25 }}
                      >
                        <Alert
                          severity="error"
                          sx={{
                            borderRadius: "12px",
                            py: 0.5,
                            fontSize: "0.83rem",
                            background: "#fef2f2",
                            border: "1px solid #fecaca",
                            "& .MuiAlert-icon": { fontSize: "16px" },
                            fontFamily: "'DM Sans', sans-serif",
                          }}
                        >
                          {error}
                        </Alert>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <motion.div
                    whileHover={{ scale: 1.015 }}
                    whileTap={{ scale: 0.985 }}
                  >
                    <Button
                      type="submit"
                      fullWidth
                      size="large"
                      variant="contained"
                      disabled={loading}
                      sx={{
                        py: 1.75,
                        borderRadius: "14px",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        fontFamily: "'DM Sans', sans-serif",
                        letterSpacing: "-0.2px",
                        background: loading
                          ? "#e2e8f0"
                          : "linear-gradient(135deg, #059669 0%, #10b981 50%, #34d399 100%)",
                        boxShadow: loading
                          ? "none"
                          : "0 8px 24px -4px rgba(16,185,129,0.45)",
                        transition: "all 0.3s ease",
                        "&:hover:not(:disabled)": {
                          background:
                            "linear-gradient(135deg, #047857 0%, #059669 100%)",
                          boxShadow: "0 12px 28px -4px rgba(16,185,129,0.55)",
                        },
                        "&:disabled": {
                          background: "#e2e8f0",
                          color: "#94a3b8",
                          boxShadow: "none",
                        },
                      }}
                    >
                      <AnimatePresence mode="wait">
                        {loading ? (
                          <motion.div
                            key="loading"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          >
                            <Stack
                              direction="row"
                              spacing={1.5}
                              alignItems="center"
                            >
                              <CircularProgress
                                size={17}
                                thickness={5}
                                sx={{ color: "#94a3b8" }}
                              />
                              <span>Signing in...</span>
                            </Stack>
                          </motion.div>
                        ) : (
                          <motion.span
                            key="login"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          >
                            View My Performance →
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </Button>
                  </motion.div>
                </Stack>
              </form>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <Box sx={{ mt: 3, pt: 2.5, borderTop: "1px solid #f1f5f9" }}>
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    color: "#94a3b8",
                    fontWeight: 500,
                  }}
                >
                  Your code is provided by your teacher or institution
                </Typography>
              </Box>
            </motion.div>
          </Paper>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          <Typography
            sx={{
              display: "block",
              textAlign: "center",
              mt: 3.5,
              color: "#b0bec5",
              fontWeight: 500,
              fontSize: "0.75rem",
              letterSpacing: "0.02em",
            }}
          >
            © {new Date().getFullYear()} LiteUpper · Academic Performance Portal
          </Typography>
        </motion.div>
      </Container>
    </Box>
  );
}
